import React from "react";
import {
	formatBytes,
	NotificationContainer,
	NotificationRenderProps,
	useNotifications,
	useUpdatingRef,
} from "@hex-insights/core";
import {
	FormState,
	FormValues,
	settings,
	SubFormBlankItemOrFunction,
	useFormSubmissionLock,
} from "@hex-insights/forms";
import { MediaAttachmentFormValues } from "../../FormUtilities";

export function useUploadFiles<
	FV extends FormValues,
	T extends FormValues & Pick<MediaAttachmentFormValues.Create, "title" | "isFile" | "fileKey" | "fileContentType">,
>(
	formState: FormState<FV>,
	addItem: (item: T) => void,
	blankItem: SubFormBlankItemOrFunction<FV, T>,
	uploadingCount: number,
	setUploadingCount?: (action: React.SetStateAction<number>) => void,
) {
	const formStateRef = useUpdatingRef(formState);

	const { addNotification } = useNotifications();

	const { setLock } = useFormSubmissionLock({
		formState,
		initialIsLocked: false,
		keyPrefix: "media-attachment-upload",
	});
	React.useEffect(() => {
		if (uploadingCount === 0) {
			setLock(false);
		} else {
			setLock(true);
		}
	}, [uploadingCount, setLock]);

	return React.useCallback(
		async (files: FileList | null) => {
			if (!files) {
				return;
			}

			if (setUploadingCount) {
				setUploadingCount((prev) => prev + 1);
			}
			await Promise.all(
				Array.from(files).map(async (file) => {
					const maxSize = settings.uploads.maxSizeBytes;
					if (maxSize > 0 && file.size > maxSize) {
						addNotification((props) => <FileTooLargeErrorNotification {...props} maxFileSizeBytes={maxSize} />, 3000);
						return;
					}

					try {
						const { ok, key, contentType } = await settings.uploads.uploadFile(file);
						if (ok) {
							const baseBlankItem =
								typeof blankItem === "function"
									? (blankItem as (formState: FormState<FV>) => T)(formStateRef.current)
									: blankItem;
							addItem({
								...baseBlankItem,
								title: file.name,
								isFile: true,
								fileKey: key,
								fileContentType: contentType,
							});
						} else {
							addNotification(FileUploadErrorNotification, 3000);
						}
					} catch {
						addNotification(FileUploadErrorNotification, 3000);
					}
				}),
			);
			if (setUploadingCount) {
				setUploadingCount((prev) => prev - 1);
			}
		},
		[formStateRef, addItem, blankItem, setUploadingCount, addNotification],
	);
}

type FileTooLargeErrorNotificationProps = NotificationRenderProps & {
	maxFileSizeBytes: number;
};

function FileTooLargeErrorNotification({ maxFileSizeBytes, ...props }: FileTooLargeErrorNotificationProps) {
	return (
		<NotificationContainer variant="danger" {...props}>
			File is too large. Please choose a file less than {formatBytes(maxFileSizeBytes)}.
		</NotificationContainer>
	);
}

function FileUploadErrorNotification(props: NotificationRenderProps) {
	return (
		<NotificationContainer variant="danger" {...props}>
			Encountered an error while uploading file. Please try again or choose another file.
		</NotificationContainer>
	);
}
